import React, { useState } from 'react';

import styled from '@emotion/styled';

import { HOME_FAQ_ITEMS } from './data';
import { EntranceHeadline } from './entrance_headline';
import {
  BodyMd,
  HeadingH3,
  HomeDesktopOnly,
  HomeMobileOnly,
  Section,
  SectionContainer,
} from './styles';
import { MEDIA, TOKENS } from './tokens';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[24]};

  ${MEDIA.lg} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${TOKENS.space[80]};
  }
`;

const Intro = styled.div`
  flex-shrink: 0;
  width: 100%;

  ${MEDIA.lg} {
    width: 360px;
    max-width: 360px;
  }
`;

const FaqTitle = styled.h1`
  margin: 0;
  font-family: ${TOKENS.font.display};
  font-weight: ${TOKENS.type.headingH2Weight};
  font-size: ${TOKENS.type.headingH2Size};
  line-height: ${TOKENS.type.headingH2LineHeight};
  letter-spacing: 0;
  color: ${TOKENS.color.foreground};
  /* Mobile: single line per Figma. Desktop uses an explicit break. */
  white-space: nowrap;

  ${MEDIA.lg} {
    white-space: normal;
  }
`;

const FaqList = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const FaqItem = styled.button`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  border: 0;
  border-bottom: 1px solid ${TOKENS.color.neutral200};
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const FaqHeader = styled.span`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${TOKENS.space[24]};
  padding: ${TOKENS.space[24]} ${TOKENS.space[8]};

  ${MEDIA.lg} {
    padding-left: ${TOKENS.space[16]};
    padding-right: ${TOKENS.space[16]};
  }
`;

const FaqQuestion = styled(HeadingH3)`
  min-width: 0;
  font-size: 20px;
  line-height: 1.3;
`;

const FaqToggle = styled.span<{ $open: boolean }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  position: relative;
  transform: rotate(${({ $open }) => ($open ? '45deg' : '0deg')});
  transition: transform 0.3s ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 14px;
    height: 2px;
    background: currentColor;
    transform: translate(-50%, -50%);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(90deg);
  }
`;

const FaqAnswerWrap = styled.span<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    grid-template-rows 0.3s ease,
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

const FaqAnswerInner = styled.span`
  overflow: hidden;
`;

const FaqAnswer = styled(BodyMd)`
  display: block;
  padding: 0 ${TOKENS.space[8]} ${TOKENS.space[24]};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${TOKENS.color.foreground};

  ${MEDIA.lg} {
    padding-left: ${TOKENS.space[16]};
    padding-right: ${TOKENS.space[16]};
    font-size: 16px;
    line-height: 24px;
  }
`;

export const HomeFaqSection: React.FC = () => {
  const [openIndices, setOpenIndices] = useState<Set<number>>(
    () => new Set([0]),
  );

  const toggleItem = (index: number) => {
    setOpenIndices((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <Section id="faq" $variant="faq">
      <SectionContainer>
        <Layout>
          <Intro>
            <EntranceHeadline as={FaqTitle}>
              <HomeMobileOnly>Frequently asked questions</HomeMobileOnly>
              <HomeDesktopOnly>
                Frequently asked
                <br />
                questions
              </HomeDesktopOnly>
            </EntranceHeadline>
          </Intro>
          <FaqList>
            {HOME_FAQ_ITEMS.map((item, index) => {
              const open = openIndices.has(index);
              return (
                <FaqItem
                  key={item.question}
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggleItem(index)}
                >
                  <FaqHeader>
                    <FaqQuestion>{item.question}</FaqQuestion>
                    <FaqToggle $open={open} aria-hidden="true" />
                  </FaqHeader>
                  <FaqAnswerWrap $open={open} aria-hidden={!open}>
                    <FaqAnswerInner>
                      <FaqAnswer>{item.answer}</FaqAnswer>
                    </FaqAnswerInner>
                  </FaqAnswerWrap>
                </FaqItem>
              );
            })}
          </FaqList>
        </Layout>
      </SectionContainer>
    </Section>
  );
};
